import "./Operations.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE, getToken } from "../../api/client";
import Card, { CardHead } from "../../components/Card/Card";
import Button from "../../components/Button/Button";
import TextField from "../../components/TextField/TextField";
import SelectField from "../../components/SelectField/SelectField";
import ConfirmAction from "../../components/ConfirmAction/ConfirmAction";
import PreviewFrame from "../../components/PreviewFrame/PreviewFrame";
import SelectionList from "../../components/SelectionList/SelectionList";
import WindowForm from "../../components/WindowForm/WindowForm";
import ResultBannerPreview from "../../preview/participant/components/ResultBanner/ResultBanner";
import Loader from "../../components/Loader/Loader";

const TABS = [
  "Windows",
  "Flags",
  "Roster",
  "Eliminations",
  "People",
  "Results",
  "Export",
];

const STATUS_LABEL = {
  active: "Active",
  pre_elimination: "Pre-elimination",
  eliminated: "Eliminated",
};

function WindowsTab() {
  const [windows, setWindows] = useState(null);
  const [editing, setEditing] = useState(undefined); 

  function load() {
    api.get("/admin/window").then(setWindows);
  }

  useEffect(load, []);

  if (editing !== undefined) {
    return (
      <div className="ops-tab col">
        <button type="button" className="ops-back t-body-sm" onClick={() => setEditing(undefined)}>
          ← All windows
        </button>
        <WindowForm
          window={editing}
          onSaved={() => {
            setEditing(undefined);
            load();
          }}
        />
      </div>
    );
  }

  return (
    <div className="ops-tab col">
      <Button small onClick={() => setEditing(null)}>New window</Button>
      {!windows ? (
        <Loader inline />
      ) : windows.length ? (
        <ul className="ops-window-list col">
          {windows.map((w) => (
            <li key={w.slot} className="ops-window-row row">
              <div className="col ops-window-info">
                <span className="t-label">{w.title}</span>
                <span className="t-mono t-dimmer">{w.slot} · {w.isEnabled ? "enabled" : "disabled"}</span>
              </div>
              <div className="row ops-window-actions">
                <Button small variant="ghost" onClick={() => setEditing(w)}>Edit</Button>
                <ConfirmAction
                  small
                  danger
                  actionLabel="Delete"
                  confirmLabel="Delete window"
                  confirmWord={w.slot}
                  summary={`This deletes the "${w.title}" window.`}
                  consequence="Submissions already made to it are kept, marked “window deleted”. The window itself cannot be brought back."
                  onConfirm={async () => {
                    await api.delete(`/admin/window/${w.slot}`);
                    load();
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="t-body-sm t-dimmer">No submission windows yet.</p>
      )}
    </div>
  );
}

function FlagRow(props) {
  const { on, title, description, onChange } = props;

  return (
    <div className="ops-flag-row row">
      <div className="col ops-flag-text">
        <span className="t-label">{title}</span>
        <span className="t-body-sm t-dimmer">{description}</span>
      </div>
      <div className="row ops-flag-actions">
        <span className={`ops-flag-state t-mono${on ? " ops-flag-state-on" : ""}`}>
          {on ? "On" : "Off"}
        </span>
        <ConfirmAction
          small
          danger={on}
          actionLabel={on ? "Turn off" : "Turn on"}
          confirmLabel={on ? "Turn it off" : "Turn it on"}
          summary={`This turns ${on ? "off" : "on"} “${title}”.`}
          consequence={props.consequence}
          onConfirm={onChange}
        />
      </div>
    </div>
  );
}

function FlagsTab() {
  const [settings, setSettings] = useState(null);
  const [teamOpensAt, setTeamOpensAt] = useState("");
  const [teamClosesAt, setTeamClosesAt] = useState("");

  function load() {
    api.get("/settings").then(setSettings);
  }

  useEffect(load, []);

  if (!settings) return <Loader inline />;

  async function toggle(key) {
    await api.post("/admin/settings", { [key]: !settings[key] });
    load();
  }

  return (
    <div className="ops-tab ops-flags col">
      <Card>
        <CardHead eyebrow="Global" title="Results and the final round" />
        <FlagRow
          on={settings.finalRoundAnnounced}
          title="Final round announced"
          description="Every non-eliminated team sees the finalist banner."
          consequence="Every team still in the event sees this change on their next page load."
          onChange={() => toggle("finalRoundAnnounced")}
        />
        <FlagRow
          on={settings.resultsPublished}
          title="Results published"
          description="Placed teams see their placement. Teams that did not place are told nothing."
          consequence="Publishing makes the podium public immediately. Check the Results tab first."
          onChange={() => toggle("resultsPublished")}
        />
        <FlagRow
          on={settings.remarksVisible}
          title="Mentor remarks visible"
          description="Teams can read the reviews mentors have written."
          consequence="Every review already written becomes readable, not just the ones written from now on."
          onChange={() => toggle("remarksVisible")}
        />
        <PreviewFrame>
          {settings.finalRoundAnnounced ? (
            <ResultBannerPreview variant="finalist" />
          ) : (
            <p className="t-body-sm t-dimmer">No banner while the flag is off.</p>
          )}
        </PreviewFrame>
      </Card>

      <Card>
        <CardHead eyebrow="Global" title="Team creation window" />
        <div className="ops-team-window-form col">
          <TextField
            label="Opens at (ISO)"
            value={teamOpensAt || settings.teamWindow.opensAt}
            onChange={setTeamOpensAt}
            mono
          />
          <TextField
            label="Closes at (ISO)"
            value={teamClosesAt || settings.teamWindow.closesAt}
            onChange={setTeamClosesAt}
            mono
          />
          <ConfirmAction
            small
            actionLabel="Update"
            confirmLabel="Update the window"
            confirmWord="CONFIRM"
            summary="This changes when teams can be created, joined or left."
            consequence="Closing the window early strands anybody who has not formed a team yet."
            onConfirm={async () => {
              await api.post("/admin/settings", {
                teamWindowOpens: new Date(teamOpensAt || settings.teamWindow.opensAt).toISOString(),
                teamWindowCloses: new Date(teamClosesAt || settings.teamWindow.closesAt).toISOString(),
              });
              load();
            }}
          />
        </div>
      </Card>
    </div>
  );
}

const ROSTER_ROLES = [
  { value: "participant", label: "Participant" },
  { value: "mentor", label: "Mentor" },
  { value: "super_admin", label: "Super admin" },
];

const roleLabel = (role) =>
  (ROSTER_ROLES.find((candidate) => candidate.value === role) || ROSTER_ROLES[0])
    .label;

function RosterRow(props) {
  const { value, role, account, onRole, onRemove } = props;

  return (
    <div className="ops-roster-row row">
      <div className="col ops-window-info">
        <span className="t-mono">{value}</span>
        <span className="t-body-sm t-dimmer">
          {account
            ? `${account.name}${account.isBlacklisted ? " · removed from the hackathon" : ""} · ${roleLabel(role)}`
            : `No account yet · ${roleLabel(role)} from their first sign-in`}
        </span>
      </div>
      <div className="row ops-window-actions">
        {ROSTER_ROLES.filter((option) => option.value !== role).map((option) => (
          <ConfirmAction
            key={option.value}
            small
            actionLabel={`Make ${option.label.toLowerCase()}`}
            confirmLabel={`Make them ${option.label.toLowerCase()}`}
            confirmWord={value}
            summary={`This changes ${value} from ${roleLabel(role).toLowerCase()} to ${option.label.toLowerCase()}.`}
            consequence={
              option.value === "super_admin"
                ? "Super admins run the event and cannot be on a team, so this is refused if they are already on one."
                : "It takes effect on their next request, with no new sign-in."
            }
            onConfirm={() => onRole(option.value)}
          />
        ))}
        <ConfirmAction
          small
          danger
          actionLabel="Remove"
          confirmLabel="Remove from roster"
          confirmWord={value}
          summary={`This takes ${value} off the roster.`}
          consequence={
            account
              ? "They are signed out of everything on their next request, and cannot sign in again until they are put back."
              : "They will not be able to sign in at all."
          }
          onConfirm={onRemove}
        />
      </div>
    </div>
  );
}

function AddToRoster(props) {
  const { title, description, placeholder, onAdd } = props;
  const [text, setText] = useState("");
  const [role, setRole] = useState("participant");

  const values = text.split("\n").map((v) => v.trim()).filter(Boolean);

  return (
    <Card>
      <CardHead eyebrow="Add" title={title} description={description} />
      <textarea
        className="ops-textarea"
        rows={4}
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="row ops-role-picker" role="group" aria-label="Role to grant">
        {ROSTER_ROLES.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`team-kind-btn${role === option.value ? " team-kind-btn-on" : ""}`}
            aria-pressed={role === option.value}
            onClick={() => setRole(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <ConfirmAction
        small
        disabled={!values.length}
        actionLabel={
          values.length > 1
            ? `Add ${values.length} as ${roleLabel(role).toLowerCase()}`
            : `Add as ${roleLabel(role).toLowerCase()}`
        }
        confirmLabel="Add to the roster"
        summary={`This puts ${
          values.length === 1 ? values[0] : `${values.length} entries`
        } on the roster as ${roleLabel(role).toLowerCase()}.`}
        consequence="An entry that is already listed has its role changed to this one."
        onConfirm={async () => {
          await onAdd(values, role);
          setText("");
        }}
      />
    </Card>
  );
}

function RosterTab() {
  const [lists, setLists] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    api.get("/admin/whitelist").then(setLists);
  }, []);

  useEffect(load, [load]);

  const trimmed = query.trim().toLowerCase();
  const keep = (value, entry) =>
    !trimmed ||
    value.toLowerCase().includes(trimmed) ||
    (entry.account ? entry.account.name.toLowerCase().includes(trimmed) : false);

  return (
    <div className="ops-tab col">
      <p className="t-body-sm t-dimmer">
        Nobody signs in unless they are on this list. Everything else is
        blocked, and taking an entry off takes effect on that account's next
        request.
      </p>

      <div className="ops-flags col">
        <AddToRoster
          title="Email addresses"
          description="One per line. Adding an address that is already listed changes its role."
          placeholder={"aarav@vit.ac.in\nmentor@multipli.fi"}
          onAdd={(values, role) =>
            api.post("/admin/whitelist/email", { values, role }).then(load)
          }
        />
        <AddToRoster
          title="Registration numbers"
          description="One per line. Matched against the last word of a Google display name."
          placeholder={"21BCE1234\n22BCE9999"}
          onAdd={(values, role) =>
            api.post("/admin/whitelist/regno", { values, role }).then(load)
          }
        />
      </div>

      {!lists ? (
        <Loader inline />
      ) : (
        <>
          <input
            className="sel-search"
            type="search"
            value={query}
            placeholder="Search the roster by address, number or name"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the roster"
          />

          <CardHead
            eyebrow={`${lists.emails.length} on the list`}
            title="By email address"
          />
          <div className="ops-window-list col">
            {lists.emails.filter((entry) => keep(entry.email, entry)).map((entry) => (
              <RosterRow
                key={entry.email}
                value={entry.email}
                role={entry.role}
                account={entry.account}
                onRole={(role) =>
                  api
                    .patch(
                      `/admin/whitelist/email/${encodeURIComponent(entry.email)}`,
                      { role },
                    )
                    .then(load)
                }
                onRemove={() =>
                  api
                    .delete(
                      `/admin/whitelist/email/${encodeURIComponent(entry.email)}`,
                    )
                    .then(load)
                }
              />
            ))}
          </div>

          <CardHead
            eyebrow={`${lists.registrationNumbers.length} on the list`}
            title="By registration number"
          />
          <div className="ops-window-list col">
            {lists.registrationNumbers
              .filter((entry) => keep(entry.regNo, entry))
              .map((entry) => (
                <RosterRow
                  key={entry.regNo}
                  value={entry.regNo}
                  role={entry.role}
                  account={entry.account}
                  onRole={(role) =>
                    api
                      .patch(
                        `/admin/whitelist/regno/${encodeURIComponent(entry.regNo)}`,
                        { role },
                      )
                      .then(load)
                  }
                  onRemove={() =>
                    api
                      .delete(
                        `/admin/whitelist/regno/${encodeURIComponent(entry.regNo)}`,
                      )
                      .then(load)
                  }
                />
              ))}
          </div>
        </>
      )}
    </div>
  );
}

function EliminationsTab() {
  const [teams, setTeams] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/admin/teams").then(setTeams).catch((e) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const matches = useCallback(
    (team, needle) =>
      team.name.toLowerCase().includes(needle) ||
      team.joinCode.toLowerCase().includes(needle) ||
      team.members.some((member) =>
        (member.email || "").toLowerCase().includes(needle),
      ),
    [],
  );

  if (error) return <p className="t-body-sm team-error-text" role="alert">{error}</p>;
  if (!teams) return <Loader inline />;

  async function act(teamIds, action, clear) {
    await api.post("/admin/teams/elimination", { teamIds, action });
    clear();
    load();
  }

  const nameFor = (ids) =>
    ids.length === 1
      ? (teams.find((team) => team.id === ids[0]) || {}).name || "1 team"
      : `${ids.length} teams`;

  return (
    <div className="ops-tab col">
      <p className="t-body-sm t-dimmer">
        Marking stages a team in pre-elimination, which looks exactly like
        active to them. Confirming is what they see.
      </p>

      <SelectionList
        label="Teams"
        items={teams}
        getId={(team) => team.id}
        labelOf={(team) => team.name}
        matches={matches}
        searchPlaceholder="Search by team name, join code or member email"
        emptyLabel="No teams match."
        filters={[
          { value: "all", label: "All" },
          { value: "active", label: "Active" },
          { value: "pre_elimination", label: "Pre-elimination" },
          { value: "eliminated", label: "Eliminated" },
        ]}
        filterOf={(team) => team.status}
        renderRow={(team) => (
          <div className="ops-sel-row row">
            <div className="col ops-window-info">
              <span className="t-label">{team.name}</span>
              <span className="t-mono t-dimmer">
                {team.joinCode} · {team.members.length} member
                {team.members.length === 1 ? "" : "s"}
              </span>
            </div>
            <span className={`team-status-tag team-status-tag-${team.status}`}>
              {STATUS_LABEL[team.status]}
            </span>
          </div>
        )}
        renderActions={(selected, clear) => (
          <>
            <ConfirmAction
              small
              actionLabel="Move to pre-elimination"
              confirmLabel="Stage them"
              confirmWord="STAGE"
              summary={`This stages ${nameFor(selected)} for elimination.`}
              consequence="Nothing changes on their dashboard. Teams that are already staged or eliminated are skipped."
              onConfirm={() => act(selected, "mark", clear)}
            />
            <ConfirmAction
              small
              danger
              actionLabel="Eliminate"
              confirmLabel="Eliminate them"
              confirmWord="ELIMINATE"
              summary={`This eliminates ${nameFor(selected)}.`}
              consequence="They see the farewell banner and no submission windows, straight away. Only teams already staged in pre-elimination are eliminated."
              onConfirm={() => act(selected, "confirm", clear)}
            />
            <ConfirmAction
              small
              actionLabel="Return to active"
              confirmLabel="Return them"
              confirmWord="RESTORE"
              summary={`This returns ${nameFor(selected)} to active.`}
              consequence="Any trace of the elimination is cleared and they see the event as normal again."
              onConfirm={() => act(selected, "clear", clear)}
            />
          </>
        )}
      />
    </div>
  );
}

function PeopleTab() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/admin/users").then(setUsers).catch((e) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const matches = useCallback(
    (user, needle) =>
      user.name.toLowerCase().includes(needle) ||
      user.email.toLowerCase().includes(needle) ||
      (user.regNo || "").toLowerCase().includes(needle),
    [],
  );

  if (error) return <p className="t-body-sm team-error-text" role="alert">{error}</p>;
  if (!users) return <Loader inline />;

  async function act(userIds, isBlacklisted, clear) {
    await api.post("/admin/users/blacklist", { userIds, isBlacklisted });
    clear();
    load();
  }

  const nameFor = (ids) =>
    ids.length === 1
      ? (users.find((user) => user.id === ids[0]) || {}).name || "1 account"
      : `${ids.length} accounts`;

  return (
    <div className="ops-tab col">
      <p className="t-body-sm t-dimmer">
        This is the per-account ban, checked before the roster is. Somebody
        removed here stays removed even if their roster entry is added back.
      </p>

      <SelectionList
        label="People"
        items={users}
        getId={(user) => user.id}
        labelOf={(user) => user.name}
        matches={matches}
        searchPlaceholder="Search by name, email or registration number"
        emptyLabel="Nobody matches."
        filters={[
          { value: "all", label: "All" },
          { value: "in", label: "In the hackathon" },
          { value: "out", label: "Removed" },
        ]}
        filterOf={(user) => (user.isBlacklisted ? "out" : "in")}
        renderRow={(user) => (
          <div className="ops-sel-row row">
            <div className="col ops-window-info">
              <span className="t-label">{user.name}</span>
              <span className="t-mono t-dimmer">
                {user.email}
                {user.regNo ? ` · ${user.regNo}` : ""} · {roleLabel(user.role)}
              </span>
            </div>
            {user.isBlacklisted ? (
              <span className="team-status-tag team-status-tag-eliminated">
                Removed
              </span>
            ) : null}
          </div>
        )}
        renderActions={(selected, clear) => (
          <>
            <ConfirmAction
              small
              danger
              actionLabel="Remove from the hackathon"
              confirmLabel="Remove them"
              confirmWord="REMOVE"
              summary={`This removes ${nameFor(selected)} from the hackathon.`}
              consequence="They are signed out on their next request and cannot sign in again. Your own account is skipped."
              onConfirm={() => act(selected, true, clear)}
            />
            <ConfirmAction
              small
              actionLabel="Let them back in"
              confirmLabel="Let them back in"
              confirmWord="RESTORE"
              summary={`This lets ${nameFor(selected)} back into the hackathon.`}
              consequence="They can sign in again straight away, provided their roster entry still stands."
              onConfirm={() => act(selected, false, clear)}
            />
          </>
        )}
      />
    </div>
  );
}

const PODIUM_SLOTS = [
  { key: "first", label: "1st" },
  { key: "second", label: "2nd" },
  { key: "third", label: "3rd" },
];

function ResultsTab() {
  const [teams, setTeams] = useState(null);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    Promise.all([api.get("/admin/teams"), api.get("/admin/podium")])
      .then(([teamList, podium]) => {
        setTeams(teamList);
        setSaved(podium);
        setDraft(podium);
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const eligible = useMemo(
    () => (teams || []).filter((team) => team.status !== "eliminated"),
    [teams],
  );

  if (error) return <p className="t-body-sm team-error-text" role="alert">{error}</p>;
  if (!draft) return <Loader inline />;

  const nameOf = (teamId) =>
    (eligible.find((team) => team.id === teamId) || {}).name || null;

  const changed = PODIUM_SLOTS.some(
    (slot) => (draft[slot.key] || "") !== (saved[slot.key] || ""),
  );

  const summary = PODIUM_SLOTS.map(
    (slot) => `${slot.label}: ${nameOf(draft[slot.key]) || "not awarded"}`,
  ).join(", ");

  return (
    <div className="ops-tab col">
      <Card>
        <CardHead
          eyebrow="Results"
          title="The podium"
          description="Pick a team for each placement. A placement can be left unawarded, and no team can hold two."
        />
        <div className="ops-podium col">
          {PODIUM_SLOTS.map((slot) => {
            const takenElsewhere = PODIUM_SLOTS.filter(
              (other) => other.key !== slot.key,
            ).map((other) => draft[other.key]);

            return (
              <SelectField
                key={slot.key}
                label={slot.label}
                placeholder="Not awarded"
                value={draft[slot.key] || ""}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, [slot.key]: value || null }))
                }
                options={eligible
                  .filter((team) => !takenElsewhere.includes(team.id))
                  .map((team) => ({ value: team.id, label: team.name }))}
              />
            );
          })}
        </div>

        <ConfirmAction
          actionLabel="Save the podium"
          confirmLabel="Save it"
          confirmWord="PODIUM"
          disabled={!changed}
          summary={`This sets the podium to ${summary}.`}
          consequence="Teams see their placement as soon as results are published, and a team that did not place is told nothing."
          onConfirm={async () => {
            await api.put("/admin/podium", {
              first: draft.first || null,
              second: draft.second || null,
              third: draft.third || null,
            });
            load();
          }}
        />
      </Card>

      <PreviewFrame>
        <ResultBannerPreview variant="result" placement="1st" />
      </PreviewFrame>
    </div>
  );
}

function ExportTab() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function download() {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/admin/export/workbook`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Export failed.");
      }
      const disposition = response.headers.get("content-disposition") || "";
      const match = /filename="([^"]+)"/.exec(disposition);
      const filename = match ? match[1] : "multipli-hackathon.xlsx";
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ops-tab col">
      <Card>
        <CardHead
          eyebrow="Export"
          title="Download the workbook"
          description="Teams, members, submissions, submission history, reviews, windows and activity, all in one .xlsx."
        />
        <Button busy={busy} busyLabel="Building…" onClick={download}>
          Download workbook
        </Button>
        {error ? <p className="t-body-sm team-error-text" role="alert">{error}</p> : null}
      </Card>
    </div>
  );
}

export default function Operations() {
  const [tab, setTab] = useState("Windows");

  return (
    <div className="ops-page col">
      <h1 className="t-h1">Operations</h1>
      <p className="t-body-sm t-dim">
        Every change made from here is confirmed twice before it is sent.
      </p>
      <div className="ops-tabs row">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`ops-tab-btn${tab === t ? " ops-tab-btn-on" : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Windows" ? <WindowsTab /> : null}
      {tab === "Flags" ? <FlagsTab /> : null}
      {tab === "Roster" ? <RosterTab /> : null}
      {tab === "Eliminations" ? <EliminationsTab /> : null}
      {tab === "People" ? <PeopleTab /> : null}
      {tab === "Results" ? <ResultsTab /> : null}
      {tab === "Export" ? <ExportTab /> : null}
    </div>
  );
}
